import { memo, useMemo, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";

interface StepsProgressBarProps {
  currentStep: number;
  labels: string[];
}

function StepsProgressBarInner({
  currentStep,
  labels,
}: StepsProgressBarProps) {
  const { i18n } = useTranslation();
  const rtlFlow = i18n.dir() === "rtl";

  const stepStates = useMemo(
    () =>
      labels.map((_, index) => {
        if (index < currentStep) return "completed" as const;
        if (index === currentStep) return "current" as const;
        return "upcoming" as const;
      }),
    [labels, currentStep],
  );

  return (
    <nav className="mb-4" dir="ltr" aria-label="Progress">
      <ol
        className={`list-unstyled d-flex mb-0 w-100 position-relative${
          rtlFlow ? " flex-row-reverse" : ""
        }`}
        style={
          {
            "--step-count": labels.length,
            "--registration-step-badge-size": "2rem",
          } as CSSProperties
        }
      >
        <div
          className="position-absolute z-0 border-top border-secondary pe-none"
          style={{
            top: "calc(var(--registration-step-badge-size) / 2)",
            left: "calc((50% / var(--step-count)) + (var(--registration-step-badge-size) / 2))",
            right: "calc((50% / var(--step-count)) + (var(--registration-step-badge-size) / 2))",
            height: 0,
          }}
          aria-hidden
        />
        {labels.map((label, index) => {
          const state = stepStates[index];

          return (
            <li
              key={`step-${index}`}
              className="flex-grow-1 d-flex flex-column align-items-center position-relative min-w-0 z-1"
            >
              <span
                className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 border border-2 fw-semibold small lh-1 ${
                  state === "completed"
                    ? "bg-success text-white border-success"
                    : state === "current"
                      ? "bg-primary text-white border-primary"
                      : "text-white"
                }`}
                style={{
                  width: "var(--registration-step-badge-size)",
                  height: "var(--registration-step-badge-size)",
                  ...(state === "upcoming"
                    ? { backgroundColor: "#9e9e9e", borderColor: "#9e9e9e" }
                    : {}),
                }}
                aria-current={state === "current" ? "step" : undefined}
              >
                {state === "completed" ? (
                  <i className="bi bi-check-lg" aria-hidden />
                ) : (
                  index + 1
                )}
              </span>
              <span
                className={`d-block w-100 small mt-2 px-1 text-center lh-sm ${
                  state === "completed"
                    ? "text-success fw-semibold"
                    : state === "current"
                      ? "text-primary fw-bold"
                      : "text-muted"
                }`}
                style={{ textWrap: "balance" }}
                dir="auto"
              >
                <span className="d-none d-sm-inline">{label}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

StepsProgressBarInner.displayName = "StepsProgressBar";

const StepsProgressBar = memo(StepsProgressBarInner);
StepsProgressBar.displayName = "StepsProgressBar";

export default StepsProgressBar;
